import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import InputNumber from "../../../../components/InputNumber/InputNumber";
import InputShortText from "../../../../components/InputShortText/InputShortText";
import Notice from "../../../../components/Notice/Notice";
import SecondaryButton from "../../../../components/SecondaryButton/SecondaryButton";
import SmallCheckBox from "../../../../components/SmallCheckBox/SmallCheckBox";
import Subtitle from "../../../../components/Subtitle/Subtitle";
import TextArea from "../../../../components/TextArea/TextArea";
import { EmailHtmlDualEditor } from "../../../../components/EmailHtmlDualEditor/EmailHtmlDualEditor";
import { NotificationDeliveryToggle } from "../../../../components/NotificationDeliveryToggle/NotificationDeliveryToggle";
import TinyText from "../../../../components/TinyText/TinyText";
import RemindersAPI from "../../../../api/RemindersAPI";
import { toastService } from "../../../../services/ToastService";
import { normalizeEmailBodyFormat, type EmailBodyFormat } from "../../../../types/emailBodyFormat";
import { isValidText } from "../../../../shared/validation";
import { UpdateCallback } from "../../sections/NotificationsSegment";
import type { NotificationChannel } from "../../../../components/NotificationChannelToggle/NotificationChannelToggle";
import { stableSignatureBrandIntro } from "../reminderConfigStableSignature";

export interface BrandIntroReminderConfig {
  enabled: boolean;
  /** Always email in the product today; kept for parity with other reminder configs. */
  type?: NotificationChannel;
  emailTitle?: string;
  text?: string;
  daysBeforeVisit?: number;
  emailBodyFormat?: EmailBodyFormat;
}

interface Props {
  update: UpdateCallback;
  defaultConfig?: BrandIntroReminderConfig;
  hideSectionTitle?: boolean;
}

export function BrandIntroReminder({ update, defaultConfig, hideSectionTitle }: Props) {
  const [enabled, setEnabled] = useState(defaultConfig?.enabled ?? false);
  const [emailTitle, setEmailTitle] = useState(defaultConfig?.emailTitle ?? "");
  const [text, setText] = useState(defaultConfig?.text ?? "");
  const [daysBeforeVisit, setDaysBeforeVisit] = useState(defaultConfig?.daysBeforeVisit ?? 3);
  const [emailBodyFormat, setEmailBodyFormat] = useState<EmailBodyFormat | undefined>(
    defaultConfig?.emailBodyFormat
  );
  const [testLoading, setTestLoading] = useState(false);
  const { t } = useTranslation();
  const lastEmittedSignatureRef = useRef<string | null>(null);

  const fmt = normalizeEmailBodyFormat(emailBodyFormat);

  useEffect(() => {
    if (!defaultConfig) return;
    const incoming = stableSignatureBrandIntro({
      enabled: defaultConfig.enabled ?? false,
      type: defaultConfig.type,
      emailTitle: defaultConfig.emailTitle,
      text: defaultConfig.text,
      daysBeforeVisit: defaultConfig.daysBeforeVisit,
      emailBodyFormat: defaultConfig.emailBodyFormat,
    });
    if (lastEmittedSignatureRef.current !== null && incoming === lastEmittedSignatureRef.current) {
      return;
    }
    setEnabled(defaultConfig.enabled ?? false);
    setEmailTitle(defaultConfig.emailTitle ?? "");
    setText(defaultConfig.text ?? "");
    setDaysBeforeVisit(defaultConfig.daysBeforeVisit ?? 3);
    setEmailBodyFormat(defaultConfig.emailBodyFormat);
  }, [defaultConfig]);

  useEffect(() => {
    const payload = {
      enabled,
      type: "email" as const,
      emailTitle,
      text,
      daysBeforeVisit,
      emailBodyFormat: fmt,
    };
    lastEmittedSignatureRef.current = stableSignatureBrandIntro(payload);
    update("brandIntroReminder", payload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, emailTitle, text, daysBeforeVisit, fmt]);

  const injectDefaultTemplate = () => {
    setEmailTitle(String(t("notifications.brand_intro.default_title")));
    setText(
      String(
        fmt === "html"
          ? t("notifications.brand_intro.default_html")
          : t("notifications.brand_intro.default_plain")
      )
    );
  };

  const sendTest = async () => {
    if (!emailTitle.trim() || !text.trim()) {
      toastService.showToast(t("notifications.test_email_validation"), "error");
      return;
    }
    setTestLoading(true);
    try {
      await RemindersAPI.sendTestEmail({
        subject: emailTitle.trim(),
        htmlBody: text,
        emailBodyFormat: fmt,
      });
      toastService.showToast(t("notifications.test_email_ok"));
    } catch {
      toastService.showToast(t("notifications.test_email_fail"), "error");
    } finally {
      setTestLoading(false);
    }
  };

  return (
    <div>
      {!hideSectionTitle && <Subtitle text={t("notifications.brand_intro")} />}
      <TinyText text={t("notifications.brand_intro.description")} />
      <SmallCheckBox
        label={t("global.enabled")!}
        onChange={() => setEnabled((prev) => !prev)}
        defaultValue={enabled}
      />
      <br />
      <InputNumber
        label={t("notifications.brand_intro.days_before")!}
        validationFn={(value) => !!value && value >= 1}
        onChange={(e) => setDaysBeforeVisit(Number(e.currentTarget.value))}
        defaultValue={daysBeforeVisit}
        errorMessage={"errors.minimum_zero"}
      />
      <div className="mt-3">
        <p className="text-sm font-extrabold">{t("notifications.delivery.label")}:</p>
        <NotificationDeliveryToggle
          variant="three_sms_disabled"
          className="mt-2"
          value={fmt}
          onChange={setEmailBodyFormat}
        />
      </div>
      <Notice pillText={t("global.notice")} tinyText={t("notifications.brand_intro.keywords")} />
      <div className="mt-3 space-y-3">
        <InputShortText
          label={t("notifications.brand_intro.email_title")!}
          validationFn={(value) => value === "" || isValidText(value, 1, 200)}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmailTitle(e.target.value)}
          errorMessage={"errors.too_long"}
          defaultValue={emailTitle}
        />
        {fmt === "plain" ? (
          <TextArea
            label={t("notifications.brand_intro.email_plain_label")!}
            onChange={(e) => setText(e.currentTarget.value)}
            defaultValue={text}
            errorMessage={"errors.too_long"}
            maxLength={10000}
          />
        ) : (
          <div className="mt-1">
            <EmailHtmlDualEditor
              label={t("notifications.brand_intro.email_html")!}
              value={text}
              onChange={setText}
              maxLength={100000}
            />
          </div>
        )}
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <SecondaryButton
          text={String(t("notifications.brand_intro.use_default_template"))}
          fn={injectDefaultTemplate}
        />
        <SecondaryButton
          text={String(t("notifications.test_email"))}
          fn={sendTest}
          isDisabled={testLoading}
        />
      </div>
    </div>
  );
}
